import { Button, FullScreenLoading } from '@civilgrid/ui/components';
import { Link } from '@tanstack/react-router';
import Dinero from 'dinero.js';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import { printBillingSummary as apiPrintBillingSummary } from '~/api/organizations2';
import type { ChartPoint, TimeRange } from '~/common/charts/types';
import { getReasonForError } from '~/common/getReasonForError';
import { notifyExportQueued } from '~/common/notifyExportQueued';
import { capturePosthogEvent, captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { Dropdown } from '~/components2/Dropdown';
import Icon from '~/components2/Icon';
import Notify from '~/components2/Notify';
import { SendEmailModal } from '~/components2/SendEmailModal';
import rootStore from '~/store';
import SimpleBarChart from '../../components2/dashboard/charts/SimpleBarChart';
import { MetricCard } from '../../components2/dashboard/widgets/MetricCard';
import ProtectedRouteLayout from '../common/ProtectedRouteLayout';
import billingSummaryStore, { type ProjectTransaction } from './store';

const formatYAxisLabel = (amount: number): string => {
  try {
    // Handle invalid input
    if (amount == null || Number.isNaN(amount)) {
      return '$0';
    }

    const dineroAmount = Dinero({ amount: Math.round(amount) });
    const cents = dineroAmount.getAmount();

    // (100M cents = $1M)
    if (cents >= 100000000) {
      return `${Dinero({ amount: Math.round(amount / 1000000) }).toFormat('$0')}M`;
    }
    // (100K cents = $1K)
    if (cents >= 100000) {
      return `${Dinero({ amount: Math.round(amount / 1000) }).toFormat('$0')}K`;
    }

    return dineroAmount.toFormat('$0');
  } catch (error) {
    console.error('Error formatting y-axis label:', error);
    return '$0';
  }
};

export default function BillingSummary() {
  const { loading, error, chartData, timeRange, allTransactions } = useSnapshot(billingSummaryStore);

  const { currentOrganizationDetail } = useSnapshot(rootStore);

  useEffect(() => {
    if (!currentOrganizationDetail.isLoading && currentOrganizationDetail.id) {
      billingSummaryStore.fetchDashboardData(currentOrganizationDetail.id);
      return () => {
        billingSummaryStore.reset();
      };
    }
  }, [currentOrganizationDetail]);

  if (currentOrganizationDetail.isLoading) {
    return <div>Loading organization...</div>;
  }

  const organizationId = currentOrganizationDetail.id;

  if (loading) return <FullScreenLoading />;
  if (error) return <div>Error: {error}</div>;
  if (!chartData) return null;

  const transactionMetricsArray = [...chartData.data.timeSeries] as ChartPoint[];

  const transactionsArray = [...chartData.data.transactions] as ProjectTransaction[];

  // get all available month/year options for dropdown
  const getMonthYearOptions = (transactions: readonly ProjectTransaction[]) => {
    const uniqueDates = [
      ...new Set(
        transactions.map((tx) => {
          const date = new Date(tx.createdAt);
          // Format YYYY-MM with proper month number (adding 1 since getMonth() is 0-based)
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        }),
      ),
    ]
      .sort()
      .reverse();

    return [
      { label: 'All Time', value: 'all' },
      ...uniqueDates.map((dateStr) => {
        // Parse the YYYY-MM string into year and month
        const [year, month] = dateStr.split('-').map(Number);
        // Create date on the first of the month to avoid timezone issues
        const date = new Date(year, month - 1, 1);
        return {
          label: date.toLocaleString('en-US', {
            month: 'long',
            year: 'numeric',
          }),
          value: dateStr,
        };
      }),
    ];
  };

  const timeRangeOptions = getMonthYearOptions(allTransactions).map((option) => ({
    type: 'simple' as const,
    label: option.label,
    value: option.value,
    onClick: () => billingSummaryStore.updateTimeRange(currentOrganizationDetail.id, option.value as TimeRange),
  }));

  // Get current selected date label
  const currentTimeRangeLabel =
    timeRange === 'all' ? 'All Time' : DateTime.fromFormat(timeRange, 'yyyy-MM').toFormat('MMMM yyyy');

  // Sort transactions by date descending (most recent first)
  const sortedTransactions = [...transactionsArray].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <ProtectedRouteLayout includeOrgProjectSwitcher={false} includeSearch={false}>
      <div className="flex h-screen w-full justify-center overflow-hidden">
        <div className="flex w-[80vw] flex-col">
          <div className="flex h-16 items-center justify-between py-1">
            <div className="text-xl font-normal">{currentOrganizationDetail.name} - Billing Dashboard</div>

            <Dropdown
              items={timeRangeOptions}
              triggerElement={
                <div
                  data-testid="billing-summary-time-range-trigger"
                  className="flex cursor-pointer flex-row items-center justify-center space-x-2 rounded-md border bg-white px-2 py-1 hover:bg-gray-50"
                >
                  <Icon icon="calendar" />
                  <div>{currentTimeRangeLabel}</div>
                </div>
              }
            />
          </div>

          {/* Main content area - flexible height with overflow */}
          <div className="flex flex-1 flex-col gap-4 overflow-hidden">
            {/* Charts section */}
            <div className="grid grid-cols-4 gap-4">
              {/* Metrics cards */}
              <div className="flex h-[40vh] flex-col gap-2">
                <MetricCard
                  title="Total Spend"
                  value={Dinero({
                    amount: Math.round(Number(chartData.data.summary.totalSpend)),
                  }).toFormat('$0,0')}
                  iconName="currency"
                />
                <MetricCard
                  title="Total Transactions"
                  value={chartData.data.summary.totalTransactions}
                  iconName="activity"
                />
              </div>

              {/* Bar chart */}
              <div className="col-span-3 h-[40vh]">
                <SimpleBarChart
                  data={transactionMetricsArray}
                  title="Total Spend"
                  timeRangeLabel={currentTimeRangeLabel}
                  yAxisLabel="($) Spend"
                  yAxisFormatter={formatYAxisLabel}
                  tooltipFormatter={(value) => [Dinero({ amount: value }).toFormat('$0,0.00'), 'Total Spend, ($)']}
                />
              </div>
            </div>

            {/* Transaction table section - remaining height with scroll */}
            <div className="flex-1 overflow-hidden pb-20">
              <TransactionTable transactions={sortedTransactions} organizationId={organizationId} />
            </div>
          </div>
        </div>
      </div>
    </ProtectedRouteLayout>
  );
}

type TransactionTableProps = {
  transactions: ProjectTransaction[];
  organizationId: string;
};

const TransactionTable = ({ transactions, organizationId }: TransactionTableProps) => {
  const { timeRange } = useSnapshot(billingSummaryStore);
  const [isLoading, setIsLoading] = useState(false);
  const [printType, setPrintType] = useState<'docx' | 'pdf' | null>(null);

  const handlePrintSummary = async (
    organizationId: string,
    dateRange: string,
    type: 'docx' | 'pdf',
    ccEmails: string[] | undefined,
  ) => {
    try {
      setIsLoading(true);
      await apiPrintBillingSummary(organizationId, dateRange, type, ccEmails);

      notifyExportQueued({
        title: 'Billing Summary',
        message: 'Your billing summary request is being processed. You will receive the file via email shortly.',
        ccEmails,
      });

      capturePosthogEvent(POSTHOG_EVENTS.organizationBillingSummaryPrinted, {
        organization_id: organizationId,
        date_range: dateRange,
        type,
      });
    } catch (err) {
      captureTypedPosthogEvent(POSTHOG_EVENTS.organizationBillingSummaryPrintFailed, {
        organization_id: organizationId,
        date_range: dateRange,
        type,
        reason: getReasonForError(err),
      });
      Notify.error({
        title: 'Error Billing Summary',
        message: 'Error printing billing summary. Please try again or contact support.',
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const printModal = (
    <SendEmailModal
      open={printType !== null}
      onOpenChange={(open) => {
        if (!open) setPrintType(null);
      }}
      title="Print billing summary"
      testIdPrefix="billing-summary-print"
      onSend={(ccEmails) =>
        printType &&
        handlePrintSummary(organizationId, timeRange, printType, ccEmails.length > 0 ? ccEmails : undefined)
      }
    />
  );

  return (
    <>
      {printModal}
      {isLoading ? (
        <FullScreenLoading />
      ) : (
        <div className="flex h-full flex-col">
          <div className="mt-4 flex flex-row items-center justify-between py-4">
            <div className="text-lg">Transaction History</div>
            <Dropdown
              side="bottom"
              align="start"
              triggerElement={<Button data-testid="billing-summary-print-button">Print Summary</Button>}
              items={[
                {
                  type: 'simple',
                  label: 'DOC (.docx)',
                  onClick: () => setPrintType('docx'),
                },
                {
                  type: 'simple',
                  label: 'PDF (.pdf)',
                  onClick: () => setPrintType('pdf'),
                },
              ]}
            />
          </div>

          {/* Table Header - fixed */}
          <div className="sticky top-0 grid grid-cols-8 text-sm">
            <div className="col-span-1">Date</div>
            <div className="col-span-2">Project</div>
            <div className="col-span-4">Description</div>
            <div className="col-span-1">Amount</div>
          </div>

          {/* Table Body - scrollable */}
          <div className="flex-1 overflow-y-auto">
            {transactions.map((transaction) => {
              const formattedAmount = Dinero({
                amount: transaction.amount,
              }).toFormat();
              const isCredit = transaction.type === 'credit' || transaction.type === 'discount';
              const displayAmount = isCredit ? `(${formattedAmount})` : formattedAmount;

              return (
                <div key={transaction.id} className="grid grid-cols-8 py-1 text-sm">
                  <div className="col-span-1">{DateTime.fromISO(transaction.createdAt).toFormat('MM/dd/yy')}</div>
                  <div className="col-span-2 cursor-pointer truncate">
                    <Link
                      to="/projects/$projectId"
                      params={{ projectId: transaction.projectId }}
                      className="flex cursor-pointer text-blue-50 underline hover:text-blue-60"
                      data-testid={`billing-summary-project-link-${transaction.projectId}`}
                    >
                      <span>
                        {/* only show billingCodes in (code1, code2) format if both are present */}
                        {`${transaction.projectName ?? ''} ${
                          transaction.billingCode
                            ? `(${[transaction.billingCode, transaction.billingCode2].filter(Boolean).join(', ')})`
                            : ''
                        }`}
                      </span>
                    </Link>
                  </div>
                  <div className="col-span-4 truncate">{transaction.description}</div>
                  <div className="col-span-1">{displayAmount}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};
